import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError, api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { usePageMeta } from '../../lib/seo';
import { brl } from '../../lib/format';
import { Alert, Button, Card, Field, Input, Select, useToast } from '../../components/ui';
import { LogoMark } from '../../components/Logo';
import { tr } from '../../lib/i18n';

type FieldErrors = Record<string, string | undefined>;

interface DerivedError {
  general: string | null;
  fields: FieldErrors;
}

function deriveError(err: unknown): DerivedError {
  if (!(err instanceof ApiError)) {
    return { general: tr('Não foi possível concluir. Tente novamente.'), fields: {} };
  }
  const fields: FieldErrors = {};
  let general: string | null = err.message || tr('Não foi possível concluir a operação.');

  const details = err.details;
  const assign = (key: string, value: unknown): void => {
    if (typeof value === 'string') fields[key] = value;
    else if (Array.isArray(value) && value.every((v) => typeof v === 'string')) {
      fields[key] = (value as string[]).join(' ');
    }
  };
  if (details && typeof details === 'object') {
    if (Array.isArray(details)) {
      for (const issue of details) {
        if (issue && typeof issue === 'object') {
          const path = (issue as { path?: unknown[] }).path;
          const message = (issue as { message?: string }).message;
          const key = Array.isArray(path) && path.length ? String(path[path.length - 1]) : '';
          if (key && message) fields[key] = message;
        }
      }
    } else {
      const record = details as Record<string, unknown>;
      const container = record.fields && typeof record.fields === 'object'
        ? (record.fields as Record<string, unknown>)
        : record;
      for (const [k, v] of Object.entries(container)) assign(k, v);
    }
  }

  switch (err.code) {
    case 'email_taken':
      fields.email = err.message;
      general = null;
      break;
    case 'terms_required':
      fields.acceptTerms = err.message;
      general = null;
      break;
    case 'invalid_category':
      fields.categoryKey = err.message;
      general = null;
      break;
    default:
      break;
  }
  return { general, fields };
}

// Categorias espelham exatamente as chaves de service_categories (apps/api/src/db/seed.ts).
// O backend não expõe uma rota pública de listagem, então usamos as chaves do seed.
const CATEGORIES = [
  { key: 'manicure', label: 'Manicure e pedicure' },
  { key: 'cabelo', label: 'Cabelo' },
  { key: 'nail_design', label: 'Nail design' },
  { key: 'barbearia', label: 'Barbearia' },
  { key: 'estetica', label: 'Estética' },
  { key: 'sobrancelhas', label: 'Design de sobrancelhas' },
  { key: 'cilios', label: 'Lash design' },
  { key: 'maquiagem', label: 'Maquiagem' },
  { key: 'depilacao', label: 'Depilação' },
  { key: 'bem_estar', label: 'Bem-estar e spa' },
  { key: 'tatuagem', label: 'Tatuagem e piercing' },
  { key: 'banho_tosa', label: 'Banho e tosa' },
  { key: 'fisioterapia', label: 'Fisioterapia' },
  { key: 'massoterapia', label: 'Massoterapia' },
  { key: 'personal_trainer', label: 'Personal trainer' },
  { key: 'podologia', label: 'Podologia' },
  { key: 'odontologia', label: 'Odontologia' },
  { key: 'aulas_consultoria', label: 'Aulas e consultoria' },
  { key: 'outros', label: 'Outros' }
];

// planKey é enum no backend: apenas estas três chaves são aceitas.
const FALLBACK_PLANS = [
  { key: 'start', name: 'AgendaPro Start', priceMonthCents: 2990 },
  { key: 'pro', name: 'AgendaPro Pro', priceMonthCents: 5990 },
  { key: 'business', name: 'AgendaPro Business', priceMonthCents: 9990 }
];

interface PlanOption {
  key: string;
  name: string;
  priceMonthCents: number;
}

interface PlansResponse {
  trialDays: number;
  plans: { key: string; name: string; priceMonthCents: number }[];
}

export function SignupProfessionalPage(): JSX.Element {
  usePageMeta(
    tr('Começar teste grátis'),
    tr('Crie sua conta profissional na AgendaPro. Seu negócio. Sua agenda. Seu tempo.'),
    '/comecar'
  );

  const { refresh } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [categoryKey, setCategoryKey] = useState('');
  const [planKey, setPlanKey] = useState('start');
  const [acceptTerms, setAcceptTerms] = useState(false);

  const [plans, setPlans] = useState<PlanOption[]>(FALLBACK_PLANS);
  const [trialDays, setTrialDays] = useState(7);
  const [submitting, setSubmitting] = useState(false);
  const [general, setGeneral] = useState<string | null>(null);
  const [fields, setFields] = useState<FieldErrors>({});

  useEffect(() => {
    let cancelled = false;
    api
      .get<PlansResponse>('/api/plans')
      .then((res) => {
        if (cancelled) return;
        const allowed = new Set(['start', 'pro', 'business']);
        const list = res.plans
          .filter((p) => allowed.has(p.key))
          .map((p) => ({ key: p.key, name: p.name, priceMonthCents: p.priceMonthCents }));
        if (list.length) setPlans(list);
        if (typeof res.trialDays === 'number' && res.trialDays > 0) setTrialDays(res.trialDays);
      })
      .catch(() => {
        /* mantém o fallback do seed */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const clientErrors = useMemo<FieldErrors>(() => {
    const e: FieldErrors = {};
    if (fullName.trim().length < 2) e.fullName = tr('Informe seu nome completo.');
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) e.email = tr('Informe um e-mail válido.');
    if (password.length < 8) e.password = tr('A senha deve ter ao menos 8 caracteres.');
    if (businessName.trim().length < 2) e.businessName = tr('Informe o nome do negócio.');
    if (!categoryKey) e.categoryKey = tr('Selecione a categoria do seu negócio.');
    if (!acceptTerms) e.acceptTerms = tr('É necessário aceitar os termos de uso para criar a conta.');
    return e;
  }, [fullName, email, password, businessName, categoryKey, acceptTerms]);

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (Object.keys(clientErrors).length > 0) {
      setFields(clientErrors);
      setGeneral(null);
      return;
    }
    setSubmitting(true);
    setGeneral(null);
    setFields({});
    try {
      await api.post('/api/auth/signup/professional', {
        fullName: fullName.trim(),
        email: email.trim(),
        password,
        phone: phone.trim(),
        businessName: businessName.trim(),
        categoryKey,
        planKey,
        acceptTerms
      });
      await refresh();
      toast(tr('Conta criada! Seu teste de {days} dias começou.', { days: trialDays }), 'success');
      navigate('/painel/configurar');
    } catch (err) {
      const derived = deriveError(err);
      setGeneral(derived.general);
      setFields(derived.fields);
      if (derived.general) toast(derived.general, 'danger');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="mx-auto w-full max-w-content px-4 py-10 sm:py-14">
      <div className="mx-auto grid w-full max-w-lg gap-6">
        <div className="flex flex-col items-center gap-3 text-center">
          <LogoMark size={48} />
          <h1 className="text-3xl">{tr("Começar teste grátis")}</h1>
          <p className="nl-hint">{tr("Seu negócio. Sua agenda. Seu tempo.")}</p>
        </div>

        <Alert tone="info" title={`Teste de ${trialDays} dias`}>
          <p>
            {tr("Seu teste gratuito de")} {trialDays} {tr("dias")} <strong>{tr("começa agora, ao criar a conta")}</strong>{tr(". Não exigimos cartão de crédito e você pode cancelar quando quiser.")}
          </p>
        </Alert>

        <Card as="section">
          <form onSubmit={(e) => void onSubmit(e)} className="flex flex-col gap-4" noValidate>
            {general ? <Alert tone="danger" title={tr("Não foi possível criar sua conta")}>{general}</Alert> : null}

            <Field label={tr("Nome completo")} error={fields.fullName} required>
              {(p) => (
                <Input
                  autoComplete="name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  disabled={submitting}
                  invalid={Boolean(fields.fullName)}
                  {...p}
                />
              )}
            </Field>

            <Field label={tr("E-mail")} error={fields.email} required>
              {(p) => (
                <Input
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={submitting}
                  invalid={Boolean(fields.email)}
                  {...p}
                />
              )}
            </Field>

            <Field
              label={tr("Senha")}
              hint="Mínimo de 8 caracteres."
              error={fields.password}
              required
            >
              {(p) => (
                <Input
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={submitting}
                  invalid={Boolean(fields.password)}
                  {...p}
                />
              )}
            </Field>

            <Field label={tr("Telefone")} hint="Opcional. Usado para contato e lembretes (WhatsApp)." error={fields.phone}>
              {(p) => (
                <Input
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="(11) 98765-4321"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  disabled={submitting}
                  invalid={Boolean(fields.phone)}
                  {...p}
                />
              )}
            </Field>

            <Field label={tr("Nome do negócio")} error={fields.businessName} required>
              {(p) => (
                <Input
                  autoComplete="organization"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  disabled={submitting}
                  invalid={Boolean(fields.businessName)}
                  {...p}
                />
              )}
            </Field>

            <Field
              label={tr("Categoria do negócio")}
              hint="A categoria define o segmento do seu negócio e orienta os serviços iniciais."
              error={fields.categoryKey}
              required
            >
              {(p) => (
                <Select
                  value={categoryKey}
                  onChange={(e) => setCategoryKey(e.target.value)}
                  disabled={submitting}
                  {...p}
                >
                  <option value="">{tr("Selecione uma categoria…")}</option>
                  {CATEGORIES.map((c) => (
                    <option key={c.key} value={c.key}>
                      {tr(c.label)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            <Field
              label={tr("Plano desejado para o teste")}
              hint="Escolha o plano que você quer avaliar no período de teste. Você pode mudar depois."
              error={fields.planKey}
              required
            >
              {(p) => (
                <Select
                  value={planKey}
                  onChange={(e) => setPlanKey(e.target.value)}
                  disabled={submitting}
                  {...p}
                >
                  {plans.map((pl) => (
                    <option key={pl.key} value={pl.key}>
                      {pl.name} — {brl(pl.priceMonthCents)}{tr("/mês")}
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            <div>
              <label className="flex cursor-pointer items-start gap-3 text-sm">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 accent-[var(--nl-graphite)]"
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  disabled={submitting}
                  aria-invalid={Boolean(fields.acceptTerms)}
                />
                <span>
                  {tr("Li e aceito os")}{' '}
                  <Link to="/termos" className="font-medium text-graphite underline" target="_blank" rel="noreferrer">
                    {tr("Termos de uso")}
                  </Link>{' '}
                  {tr("e a")}{' '}
                  <Link to="/privacidade" className="font-medium text-graphite underline" target="_blank" rel="noreferrer">
                    {tr("Política de privacidade")}
                  </Link>
                  . <span className="text-danger">*</span>
                </span>
              </label>
              {fields.acceptTerms ? (
                <p className="mt-1 text-sm text-danger">{fields.acceptTerms}</p>
              ) : null}
            </div>

            <Button type="submit" loading={submitting} disabled={submitting || !acceptTerms} className="w-full">
              {tr("Criar conta e começar teste grátis")}
            </Button>

            <p className="text-center text-sm text-muted">
              {tr("Já tem conta?")}{' '}
              <Link to="/entrar" className="font-medium text-graphite hover:underline">
                {tr("Entrar")}
              </Link>
            </p>
          </form>
        </Card>
      </div>
    </section>
  );
}
